import { Segment } from "@beacx/peacx";
import { ClipUIModel } from "models/SoundClip";
import { IAcxButtonProps } from "../AcxButton";
import { IconButtonProps } from "@mui/material";
import SoundClipEditorStore from "components/SoundClipEditor/Stores/SoundClipEditorStore";
import { observer } from "mobx-react";
import SoundClipEditorV2 from "components/SoundClipEditor/SoundClipEditorV2";
import React, { useEffect, useState } from "react";
import { AcxTranscriptionStore } from "./Stores/AcxTranscriptionStore";
import { useStore } from "utils/useStore";

type PlayerOptions = {
    id: string;
    isPlaying: boolean;
    startTime: number;
    endTime: number;
};

type PartialSegment = Partial<Segment> & { transcriptionText?: string };

type Props = {
    clip?: ClipUIModel;
    id: string;
    clipBlob?: Blob;
    clipAudioBuffer?: AudioBuffer;
    alternateClipDownloader?: () => Promise<ArrayBuffer>;
    onClipLoading?: (id: string, isLoading: boolean) => void;
    onAudioBufferReady?: ({
        id,
        index,
        audioBuffer,
        blob,
    }: {
        id: string;
        index: number;
        audioBuffer: AudioBuffer;
        blob: Blob;
    }) => void;
    onClipGenerated?: (start: number, end: number) => void;
    onClipUpdated?: (soundClip) => void;
    onSegmentMouseOver?: (
        id?: string,
        startTime?: number,
        endTime?: number,
    ) => void;
    onSegmentClick?: (id?: string | null) => void;
    segmentList?: Array<PartialSegment>;
    onClipEditorReady?: (isSuccess: boolean) => void;
    index?: number;
    editorHeight?: number;
    fullControls?: boolean;
    allowUndo?: boolean;
    noSegmentRemoval?: boolean;
    noMenu?: boolean;
    disabled?: boolean;
    minimize?: boolean;
    generateMp3?: boolean;
    maxClipDuration?: number;
    throwWarning?: boolean;
    enableZoom?: boolean;
    isMontage?: boolean;
    playerOptions?: PlayerOptions;
    disablePlaybackRate?: boolean;
    textButton?: string | null;
    textButtonProps?: Partial<IAcxButtonProps>;
    iconButton?: React.ReactNode;
    iconButtonProps?: Partial<IconButtonProps>;
    disableClip?: boolean;
    soundClipEditorStore?: SoundClipEditorStore; // this is undefined on initial render
    loadingZIndex?: number;
};

const TranscriptionLinkedSoundClipEditor: React.FC<
    React.PropsWithChildren<Props>
> = observer(({ generateMp3 = true, ...props }) => {
    const store = props.soundClipEditorStore;
    const transcriptionStore = useStore(AcxTranscriptionStore);

    useEffect(() => {
        // latestSegment is the segment the user selects
        // If that's undefined the user has effectively not selected anything via the audio
        if (
            store === undefined ||
            store?.latestSegment === undefined ||
            store.latestSegment.startTime === undefined ||
            store.latestSegment.endTime === undefined
        ) {
            transcriptionStore.setSelectedStartTimestamp(null);
            transcriptionStore.setSelectedEndTimestamp(null);
            transcriptionStore.setSelectingViaAudio(false);
            return;
        }
        // If there IS a latest segment, we are now selecting via audio
        transcriptionStore.setSelectingViaAudio(true);
        transcriptionStore.setSelectedStartTimestamp(
            store.latestSegment.startTime,
        );
        transcriptionStore.setSelectedEndTimestamp(store.latestSegment.endTime);
        if (
            store.latestSegment &&
            typeof store.latestSegment.startTime === "number"
        ) {
            const index = transcriptionStore.getScrollIndexForTimestamp(
                store.latestSegment.startTime,
            );
            transcriptionStore.setScrollIndex(index);
        }
        // We recheck this anytime isDragging changes, which is when the user isDragging along the timeline
        // Effectively re-evaluating the selected segment while they're dragging
    }, [store?.isDragging, transcriptionStore, store]);

    // Have we scrolled from the initial play already?
    // We only want to scroll when we start playing, not the whole time its playing
    const [initialScrollOnPlay, setInitialScrollOnPlay] = useState(false);

    useEffect(() => {
        if (!store || !props.playerOptions?.isPlaying) {
            return;
        }

        // Reset transcript audio highlight window when playback is started
        // from a specific blurb so previous playback range does not bleed through.
        transcriptionStore.setAudioStartTimestamp(
            props.playerOptions.startTime,
        );
        transcriptionStore.setAudioEndTimestamp(props.playerOptions.startTime);
    }, [props.playerOptions, store, transcriptionStore]);

    useEffect(() => {
        if (store?.isDragging) {
            // Reset timestamps in transcription store when dragging starts
            transcriptionStore.setSelectedStartTimestamp(null);
            transcriptionStore.setSelectedEndTimestamp(null);
        }
        // If the audio isn't playing and we click a random point on the timeline, we want to clear the selected timestamps
        if (
            store?.playHeadTime !== undefined &&
            !store.isPlaying &&
            !store.isDragging &&
            store?.playHeadTime !== store?.latestSegment?.endTime
        ) {
            // Reset timestamps in transcription store when play head time changes
            // and is not dragging or playing
            transcriptionStore.setSelectedStartTimestamp(store.playHeadTime);
            transcriptionStore.setSelectedEndTimestamp(null);
            store.setLatestSegment(undefined);
            transcriptionStore.setSelectingViaAudio(false);
            // We want to scroll on next play
            setInitialScrollOnPlay(false);
        }

        // If the audio IS playing
        if (store?.playHeadTime !== undefined && store.isPlaying) {
            if (transcriptionStore.audioStartTimestamp === null) {
                transcriptionStore.setAudioStartTimestamp(store.playHeadTime);
            }

            transcriptionStore.setAudioEndTimestamp(store.playHeadTime);

            // If our start is ever larger than the end, flip the two
            // This catches the case where you moved backwards on the timeline
            if (
                transcriptionStore.audioStartTimestamp &&
                transcriptionStore.audioEndTimestamp &&
                transcriptionStore.audioStartTimestamp >
                    transcriptionStore.audioEndTimestamp
            ) {
                const temp = transcriptionStore.audioStartTimestamp;
                transcriptionStore.setAudioStartTimestamp(
                    transcriptionStore.audioEndTimestamp,
                );
                transcriptionStore.setAudioEndTimestamp(temp);
            }

            // Have we scrolled from the initial play already?
            if (!initialScrollOnPlay) {
                const index = transcriptionStore.getScrollIndexForTimestamp(
                    store.playHeadTime,
                );
                transcriptionStore.setScrollIndex(index);
                // We use a state so we only do this scroll once
                setInitialScrollOnPlay(true);
            }

            const searchArray: {
                channel: number;
                startTime: number;
                endTime: number;
            }[] = transcriptionStore.speakerSegments;

            // Find the speaker segment we were previously on according to our audio start/end timestamps
            let previous = searchArray.find(
                (searchItem) =>
                    searchItem.channel !== undefined &&
                    searchItem.startTime <=
                        (transcriptionStore.audioStartTimestamp ?? 0) &&
                    searchItem.endTime >=
                        (transcriptionStore.audioEndTimestamp ?? 0),
            );

            // Find the speaker segment we're currently on according to the playhead time
            let current = searchArray.find(
                (searchItem) =>
                    searchItem.channel !== undefined &&
                    searchItem.startTime <= store.playHeadTime &&
                    searchItem.endTime >= store.playHeadTime,
            );

            if (
                (previous && current && previous.channel !== current.channel) ||
                (!previous && current)
            ) {
                // The current segment speaker is different from the previous segment speaker
                // Set the start time to current segment start time, end time can continue on its merry way
                transcriptionStore.setAudioStartTimestamp(current.startTime);
            }
        }
    }, [store?.playHeadTime, store, transcriptionStore, initialScrollOnPlay]);

    // handle initial render when store is undefined
    if (!store) {
        return;
    }

    return (
        <SoundClipEditorV2
            id={props.id}
            onClipLoading={props.onClipLoading}
            onAudioBufferReady={props.onAudioBufferReady}
            onClipGenerated={props.onClipGenerated}
            onClipUpdated={props.onClipUpdated}
            onSegmentMouseOver={props.onSegmentMouseOver}
            onSegmentClick={props.onSegmentClick}
            segmentList={props.segmentList}
            onClipEditorReady={props.onClipEditorReady}
            index={props.index}
            editorHeight={props.editorHeight}
            fullControls={props.fullControls}
            allowUndo={props.allowUndo}
            noSegmentRemoval={props.noSegmentRemoval}
            noMenu={props.noMenu}
            disabled={props.disabled}
            minimize={props.minimize}
            generateMp3={generateMp3}
            maxClipDuration={props.maxClipDuration}
            throwWarning={props.throwWarning}
            enableZoom={props.enableZoom}
            isMontage={props.isMontage}
            playerOptions={props.playerOptions}
            disablePlaybackRate={props.disablePlaybackRate}
            textButton={props.textButton}
            textButtonProps={props.textButtonProps}
            iconButton={props.iconButton}
            iconButtonProps={props.iconButtonProps}
            disableClip={props.disableClip}
            soundClipEditorStore={store}
            loadingZIndex={props.loadingZIndex}
            onPressPause={() => {
                transcriptionStore.setAudioStartTimestamp(null);
                transcriptionStore.setAudioEndTimestamp(null);
                setInitialScrollOnPlay(false); // Reset this so we scroll next play
            }}
            onPressPlay={() => {
                transcriptionStore.setAudioStartTimestamp(store.playHeadTime);
                transcriptionStore.setAudioEndTimestamp(store.playHeadTime);
            }}
        />
    );
});

export default TranscriptionLinkedSoundClipEditor;
